import React, { useEffect, useMemo, useState } from "react";
import poolinsLogo from "../../assets/img/poolins.jpg";
// Feuille DÉDIÉE et scopée (.pid) à la charte PoolIns Mobile. On n'importe plus
// mobileAppPages.css ici : elle est partagée avec PrivacyPolicy et l'admin des releases,
// la réutiliser aurait mélangé l'ancienne charte et la nouvelle.
import "../../assets/styles/mobileAppDownload.css";
import {
  downloadUrlForRelease,
  formatDate,
  formatFileSize,
  releaseIsEmpty,
} from "./mobileAppUtils";

/* Icônes : trait unique, `currentColor` → elles prennent la couleur de la charte
   définie sur le conteneur (plus de couleurs codées en dur comme avant). */
const svgProps = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const IconDoc = () => (
  <svg {...svgProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8" />
    <path d="M8 17h5" />
  </svg>
);

const IconAlert = () => (
  <svg {...svgProps}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconClock = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const IconCard = () => (
  <svg {...svgProps}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="7" y1="15" x2="9" y2="15" />
    <line x1="11" y1="15" x2="13" y2="15" />
  </svg>
);

const IconHeadset = () => (
  <svg {...svgProps}>
    <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
    <rect x="2" y="12" width="4" height="6" rx="1" />
    <rect x="18" y="12" width="4" height="6" rx="1" />
    <path d="M13 21c0 1-1.5 2-3 2" />
  </svg>
);

const IconShield = () => (
  <svg {...svgProps}>
    <path d="M12 2l7 4v6c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const IconDownload = () => (
  <svg {...svgProps} width="20" height="20">
    <path d="M12 3v12" />
    <polyline points="7 11 12 16 17 11" />
    <path d="M5 21h14" />
  </svg>
);

const IconCheck = () => (
  <svg {...svgProps} width="18" height="18">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconLock = () => (
  <svg {...svgProps} width="18" height="18">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const IconMail = () => (
  <svg {...svgProps} width="18" height="18">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <polyline points="3 7 12 13 21 7" />
  </svg>
);

const IconBook = () => (
  <svg {...svgProps} width="20" height="20">
    <path d="M4 4a2 2 0 0 1 2-2h11a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2z" />
    <path d="M4 18a2 2 0 0 1 2-2h12" />
    <path d="M8 7h6" />
  </svg>
);

const IconFile = () => (
  <svg {...svgProps} width="18" height="18">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const features = [
  { title: "Gestion des contrats", desc: "Souscrivez et pilotez vos contrats d’assurance en quelques clics.", icon: <IconDoc /> },
  { title: "Déclaration de sinistres", desc: "Déclarez vos sinistres rapidement avec photos et documents.", icon: <IconAlert /> },
  { title: "Suivi en temps réel", desc: "Suivez l’avancement de vos dossiers en direct.", icon: <IconClock /> },
  { title: "Paiements sécurisés", desc: "Réglez vos primes et retrouvez vos quittances sur mobile.", icon: <IconCard /> },
  { title: "Assistance client", desc: "Support direct et réactif depuis l’application.", icon: <IconHeadset /> },
  { title: "Sécurité maximale", desc: "Données protégées et connexions chiffrées.", icon: <IconShield /> },
];

const MobileAppDownloadPage = () => {
  const [release, setRelease] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | empty | error
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchLatest = async () => {
    setStatus("loading");
    setError("");
    try {
      const response = await fetch("/api/mobile-app/releases/latest");
      const contentType = response.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");
      const payload = isJson ? await response.json() : await response.text();
      if (!response.ok) {
        const message =
          typeof payload === "string" && payload.startsWith("<")
            ? "La dernière version n’est pas disponible (réponse non valide)."
            : typeof payload === "string"
              ? payload
              : payload?.message || payload?.error;
        throw new Error(message || "Impossible de récupérer la dernière version.");
      }
      const data = payload;
      if (releaseIsEmpty(data)) {
        setRelease(null);
        setStatus("empty");
        return;
      }
      setRelease(data);
      setStatus("ready");
    } catch (err) {
      setError(err?.message || "Une erreur est survenue.");
      setStatus("error");
    }
  };

  useEffect(() => {
    fetchLatest();
  }, []);

  const formattedSize = useMemo(() => formatFileSize(release?.fileSize), [release]);
  const formattedDate = useMemo(() => formatDate(release?.uploadedAt), [release]);
  const downloadUrl = downloadUrlForRelease(release);
  const releaseNotes = useMemo(
    () =>
      release?.releaseNotes
        ? release.releaseNotes
            .split(/\n|•/g)
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    [release]
  );

  const handleCopy = async () => {
    if (!release?.checksumSha256 || !navigator?.clipboard) return;
    await navigator.clipboard.writeText(release.checksumSha256);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // Le téléchargement n'est proposé QUE si une version est réellement disponible :
  // auparavant le bouton restait cliquable avec une URL vide pendant le chargement.
  const canDownload = status === "ready" && !!downloadUrl;

  // Le manuel utilisateur est FACULTATIF : le backend renvoie manuelUrl = null quand la
  // version publiée n'en a pas. On n'affiche alors simplement pas le bouton.
  const manuelUrl = status === "ready" ? release?.manuelUrl : null;

  const renderStatusBlock = (light = false) => {
    const cls = `pid-status${light ? " pid-status-light" : ""}`;
    if (status === "loading") {
      return <div className={cls}>Chargement des informations…</div>;
    }
    if (status === "error") {
      return (
        <div className={`${cls} pid-status-error`}>
          <span>{error || "Impossible de charger la dernière version."}</span>
          <button className="pid-copy" onClick={fetchLatest}>Réessayer</button>
        </div>
      );
    }
    if (status === "empty") {
      return <div className={cls}>Aucune version disponible pour le moment.</div>;
    }
    return null;
  };

  return (
    <div className="pid">
      {/* ---------- En-tête ---------- */}
      <header className="pid-nav">
        <div className="pid-wrap pid-nav-inner">
          <div className="pid-brand">
            <img src={poolinsLogo} alt="Logo PoolIns" />
            <div style={{ minWidth: 0 }}>
              <div className="pid-brand-name">PoolIns</div>
              <div className="pid-brand-sub">Votre assurance, simplement et en toute sécurité.</div>
            </div>
          </div>
          <a className="pid-navlink" href="#support">Nous contacter</a>
        </div>
      </header>

      <div className="pid-wrap">
        {/* ---------- Hero ---------- */}
        <section className="pid-hero">
          <div className="pid-hero-inner">
            <span className="pid-eyebrow">Application officielle</span>
            <h1 className="pid-h1">Téléchargez l’application PoolIns</h1>
            <p className="pid-lead">
              Gérez vos contrats, déclarez vos sinistres et suivez vos paiements
              directement depuis votre téléphone.
            </p>

            <div className="pid-cta-row">
              <a
                className={`pid-btn pid-btn-amber${canDownload ? "" : " pid-btn-disabled"}`}
                href={canDownload ? downloadUrl : undefined}
                download={canDownload || undefined}
                aria-disabled={!canDownload}
              >
                <IconDownload />
                {canDownload && release?.versionName
                  ? `Télécharger la version ${release.versionName}`
                  : "Télécharger l’application"}
              </a>
              {manuelUrl && (
                <a className="pid-btn pid-btn-ghost-inv" href={manuelUrl} download>
                  <IconBook />
                  Manuel utilisateur
                </a>
              )}
              <a className="pid-btn pid-btn-ghost-inv" href="#details">Voir les détails</a>
            </div>

            <div className="pid-trust">
              <span>Application officielle</span>
              <span className="dot">•</span>
              <span>Sécurisée</span>
              <span className="dot">•</span>
              <span>Gratuite</span>
              {/* On teste la TAILLE BRUTE, pas la valeur formatée : celle-ci vaut « N/A »
                  quand elle est absente — ce qui affichait « • N/A » dans le bandeau. */}
              {status === "ready" && release?.fileSize ? (
                <>
                  <span className="dot">•</span>
                  <span>{formattedSize}</span>
                </>
              ) : null}
            </div>

            {status !== "ready" && renderStatusBlock()}
          </div>
        </section>

        {/* ---------- Fonctionnalités ---------- */}
        <section className="pid-section">
          <div className="pid-section-head">
            <h2 className="pid-h2">Fonctionnalités clés</h2>
          </div>
          <div className="pid-features">
            {features.map((feature) => (
              <div className="pid-feature" key={feature.title}>
                <div className="pid-feature-icon" aria-hidden="true">{feature.icon}</div>
                <h4>{feature.title}</h4>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- Détails de la version ---------- */}
        <section className="pid-section" id="details">
          <div className="pid-section-head">
            <h2 className="pid-h2">Détails de la version</h2>
          </div>
          <div className="pid-card">
            {status !== "ready" ? (
              renderStatusBlock(true)
            ) : (
              <>
                <div className="pid-meta-grid">
                  <div className="pid-meta-item">
                    <div className="pid-meta-label">Version</div>
                    <div className="pid-meta-value">{release?.versionName || "N/A"}</div>
                    <div className="pid-meta-hint">Code {release?.versionCode || "-"}</div>
                  </div>
                  <div className="pid-meta-item">
                    <div className="pid-meta-label">Taille</div>
                    <div className="pid-meta-value">{formattedSize}</div>
                  </div>
                  <div className="pid-meta-item">
                    <div className="pid-meta-label">Mise à jour</div>
                    <div className="pid-meta-value">{formattedDate}</div>
                  </div>
                  <div className="pid-meta-item">
                    <div className="pid-meta-label">Compatibilité</div>
                    <div className="pid-meta-value">Android 8+</div>
                  </div>
                </div>

                <div className="pid-checksum">
                  <div className="pid-meta-label">Empreinte SHA-256</div>
                  <div className="pid-checksum-row">
                    <code>{release?.checksumSha256 || "Non fournie"}</code>
                    {release?.checksumSha256 && (
                      <button className="pid-copy" onClick={handleCopy}>
                        {copied ? "Copié" : "Copier"}
                      </button>
                    )}
                  </div>
                </div>

                {releaseNotes.length > 0 && (
                  <div className="pid-notes">
                    <div className="pid-meta-label">Notes de version</div>
                    <ul>
                      {releaseNotes.map((note, idx) => (
                        <li key={idx}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ---------- Support ---------- */}
        <section className="pid-section" id="support">
          <div className="pid-section-head">
            <h2 className="pid-h2">Support &amp; contact</h2>
          </div>
          <div className="pid-card">
            <ul className="pid-list">
              <li>
                <span className="pid-list-icon"><IconCheck /></span>
                <span>Application officielle de OSSAN ASUR</span>
              </li>
              <li>
                <span className="pid-list-icon"><IconLock /></span>
                <span>Données protégées et connexion sécurisée (HTTPS)</span>
              </li>
              <li>
                <span className="pid-list-icon"><IconMail /></span>
                <span>
                  Contact : <a href="mailto:support-apps@ossanasur.com">support-apps@ossanasur.com</a>
                </span>
              </li>
              {manuelUrl && (
                <li>
                  <span className="pid-list-icon"><IconBook /></span>
                  <span>
                    Manuel utilisateur :{" "}
                    <a href={manuelUrl} download>
                      {release?.manuelFileName || "télécharger le PDF"}
                    </a>
                  </span>
                </li>
              )}
              <li>
                <span className="pid-list-icon"><IconFile /></span>
                <a href="/privacy">Politique de confidentialité</a>
              </li>
            </ul>
          </div>
        </section>

        <footer className="pid-footer">
          OSSAN ASUR — support-apps@ossanasur.com — © {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
};

export default MobileAppDownloadPage;
